import { BoxGeometry, CylinderGeometry, Mesh, MeshStandardMaterial, PointLight, Color } from 'three';

const cube = new BoxGeometry(1, 1, 1);
const cylinder = new CylinderGeometry(1, 1, 1, 12);

const mats = {
  rail: mat(0x1a2432, 0x07111f, 0.45, 0.65),
  concrete: mat(0x263247, 0x07111f, 0.78, 0.22),
  hazard: mat(0x44202c, 0x2b0610, 0.52, 0.55),
  reactor: mat(0x19352d, 0x0a3928, 0.42, 0.4),
  glowTeal: mat(0x53ffb5, 0x53ffb5, 0.18, 0, 2.9),
  glowBlue: mat(0x6aa9ff, 0x6aa9ff, 0.18, 0, 2.4),
  glowPink: mat(0xff70d7, 0xff70d7, 0.18, 0, 2.2),
};

export function createDistrictSetPieces(world) {
  if (!world?.propRoot) return;
  clearSetPieces(world);
  const h = world.half;
  monorailWreck(world, -h * 0.42, -h * 0.34);
  reactorYard(world, h * 0.36, h * 0.28);
  marketBarricade(world, -h * 0.32, h * 0.36);
  plazaShrine(world, h * 0.32, -h * 0.28);
}

function clearSetPieces(world) {
  for (let i = world.propRoot.children.length - 1; i >= 0; i -= 1) {
    const child = world.propRoot.children[i];
    if (child.userData?.setPiece) world.propRoot.remove(child);
  }
}

function monorailWreck(world, x, z) {
  addBlock(world, 'fallen-monorail-a', x, 1.05, z, 11, 1.8, 2.4, mats.rail, -0.55);
  addBlock(world, 'fallen-monorail-b', x + 7.4, 0.7, z - 3.8, 5.2, 1.25, 2.2, mats.rail, -0.55);
  addBlock(world, 'rail-neon-cut', x + 1.2, 2.42, z - 0.1, 8.4, 0.08, 0.16, mats.glowTeal, -0.55, false);
  addLight(world, 0x53ffb5, 2.2, x + 2, 2.1, z - 1.4);
}

function reactorYard(world, x, z) {
  for (const [dx, dz] of [[-3, -2.8], [3, -2.8], [-3, 2.8], [3, 2.8]]) {
    addCylinder(world, 'reactor-pylon', x + dx, 1.6, z + dz, 0.86, 3.2, mats.reactor);
  }
  addBlock(world, 'reactor-core', x, 0.32, z, 5.8, 0.62, 2.2, mats.hazard, 0.45);
  addBlock(world, 'reactor-core-glow', x, 0.7, z, 4.6, 0.12, 0.14, mats.glowTeal, 0.45, false);
  addLight(world, 0x53ffb5, 3.6, x, 3.4, z);
}

function marketBarricade(world, x, z) {
  for (let i = 0; i < 4; i += 1) {
    addBlock(world, 'market-stall', x + i * 2.7, 0.65, z + Math.sin(i) * 1.4, 1.9, 1.3, 0.72, mats.hazard, i % 2 ? 0.22 : -0.18);
  }
  addBlock(world, 'market-sign-frame', x + 3.8, 2.4, z - 3.1, 6.2, 0.22, 0.18, mats.glowPink, 0.04, false);
  addLight(world, 0xff70d7, 2.4, x + 3.6, 2.8, z - 2.3);
}

function plazaShrine(world, x, z) {
  for (let i = 0; i < 6; i += 1) {
    const a = (i / 6) * Math.PI * 2;
    addCylinder(world, 'plaza-column', x + Math.cos(a) * 4.2, 1.15, z + Math.sin(a) * 4.2, 0.46, 2.3, mats.concrete);
  }
  addBlock(world, 'plaza-beacon', x, 1.25, z, 1.1, 2.5, 1.1, mats.glowBlue, 0, false);
  addLight(world, 0x6aa9ff, 2.8, x, 3.2, z);
}

function addBlock(world, name, x, y, z, sx, sy, sz, material, ry = 0, collide = true) {
  const mesh = new Mesh(cube, material);
  mesh.name = name;
  mesh.position.set(x, y, z);
  mesh.rotation.y = ry;
  mesh.scale.set(sx, sy, sz);
  finalize(world, mesh, sx, sz, ry, collide);
}

function addCylinder(world, name, x, y, z, radius, sy, material, collide = true) {
  const mesh = new Mesh(cylinder, material);
  mesh.name = name;
  mesh.position.set(x, y, z);
  mesh.scale.set(radius, sy, radius);
  finalize(world, mesh, radius * 2, radius * 2, 0, collide);
}

function addLight(world, color, intensity, x, y, z) {
  const light = new PointLight(color, intensity, 11, 2);
  light.position.set(x, y, z);
  light.userData.setPiece = true;
  world.propRoot.add(light);
}

function finalize(world, mesh, sx, sz, ry, collide) {
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.userData.setPiece = true;
  mesh.userData.collidable = collide;
  if (collide) {
    const c = Math.cos(ry);
    const s = Math.sin(ry);
    const hx = Math.abs(c) * sx / 2 + Math.abs(s) * sz / 2;
    const hz = Math.abs(s) * sx / 2 + Math.abs(c) * sz / 2;
    mesh.userData.radius = Math.hypot(sx, sz) * 0.5;
    mesh.userData.aabb = { minX: mesh.position.x - hx, maxX: mesh.position.x + hx, minZ: mesh.position.z - hz, maxZ: mesh.position.z + hz };
  }
  world.propRoot.add(mesh);
}

function mat(color, emissive, roughness, metalness, emissiveIntensity = 0.35) {
  return new MeshStandardMaterial({
    color,
    roughness,
    metalness,
    emissive: new Color(emissive),
    emissiveIntensity,
  });
}
